// @flow
import { gql } from 'graphql-request';
import { useCallback, useState } from 'react';

import { appClient } from '_client';
import { useAuth } from '_contexts/auth';
import { GqlError } from '_utils/errors';
import promoCodeValidators from '_utils/promoCode';
import { doRedeemPromotionCode } from '_utils/stripe';
import { useTranslation } from 'gatsby-plugin-react-i18next';

const PROMOTION_CODE = gql`
  query promotionCode($code: String!) {
    promotionCode(code: $code) {
      id
      active
      code
      coupon {
        id
        name
        amountOff
        percentOff
        valid
      }
      metadata
      constraints
    }
  }
`;

type Context = {
  currentUser?: Object,
  selectedPlan?: Object,
  type: 'checkout' | 'redeem',
};

export const getPromotionCode = async (code: string): Promise<$ReadOnlyArray<Object>> => {
  try {
    const { promotionCode } = await appClient.request(PROMOTION_CODE, { code });
    return promotionCode;
  } catch (err) {
    throw new GqlError(err);
  }
};

const usePromotionCode = (context: Context): Object => {
  const [promotionCode, setPromotionCode] = useState();
  const [error, setError] = useState();
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const { t } = useTranslation();
  const { restore } = useAuth();

  const validatePromotionCode = useCallback(
    (code: string) => {
      (async () => {
        setValidating(true);
        try {
          const promoCode = await getPromotionCode(code);

          const [valid, errorString] = promoCodeValidators({ promoCode, context });

          if (!valid) {
            throw new Error(errorString);
          }

          setPromotionCode(promoCode);
          setError(undefined);
        } catch (err) {
          setPromotionCode(undefined);
          let errMessage = err?.message ? err.message : err;
          if (errMessage === 'record not found') {
            errMessage = t('Error_Promocode_NotFound');
          }

          setError(errMessage);
        } finally {
          setValidating(false);
        }
      })();
    },
    [context]
  );

  const resetPromotionCode = useCallback(() => {
    setPromotionCode(undefined);
    setError(undefined);
  }, []);

  const redeemPromotionCode = useCallback(
    (pc: Object) =>
      (async () => {
        const promotionCodeId = pc.id;
        setRedeeming(true);
        try {
          const redeemRes = await doRedeemPromotionCode(promotionCodeId);
          const { name: promoCodeName } = redeemRes.discount?.coupon;
          setPromotionCode(undefined);
          setRedeeming(false);
          return promoCodeName;
        } catch (err) {
          setPromotionCode(undefined);
          setRedeeming(false);
          throw new GqlError(err);
        } finally {
          restore();
        }
      })(),
    [restore]
  );

  return {
    promotionCode,
    error,
    validating,
    redeeming,
    validatePromotionCode,
    resetPromotionCode,
    redeemPromotionCode,
  };
};

export default usePromotionCode;
